/** Shared types for the guest upload page. Everything tenant-specific arrives via PropertyConfig. */

/**
 * Which caption burn-in style to render, per property (config value, not a
 * build flag).
 * - 'framesafe' keeps the caption inside title-safe margins so an Aura frame
 *   cropping the photo can't clip it (default).
 * - 'framefill' composes the whole output at the frame's aspect ratio (see
 *   `frameAspect`), the photo filling the frame with the caption on a scrim
 *   panel, so the frame displays it whole with nothing left to crop. Opt-in.
 * - 'classic' is the original bottom-edge strip.
 * Absent → 'framesafe' (see DEFAULT_CAPTION_STYLE).
 */
export type CaptionStyle = "classic" | "framesafe" | "framefill";

/** Go-forward default when the config omits captionStyle. */
export const DEFAULT_CAPTION_STYLE: CaptionStyle = "framesafe";

/** Normalize a possibly-absent config value to a known CaptionStyle. */
export function resolveCaptionStyle(value: unknown): CaptionStyle {
  return value === "classic" || value === "framefill" ? value : DEFAULT_CAPTION_STYLE;
}

/** Numeric default frame ratio (width/height) matching '4:3'. */
export const DEFAULT_FRAME_ASPECT_RATIO = 4 / 3;

/**
 * Resolve a per-property frameAspect ('W:H' string) to a numeric target ratio
 * (width/height). Only meaningful for the 'framefill' style. Absent/invalid →
 * 4:3 (1.333…). Ratio > 1 is landscape, < 1 is portrait.
 */
export function resolveFrameAspectRatio(value: unknown): number {
  if (typeof value !== "string") return DEFAULT_FRAME_ASPECT_RATIO;
  const m = /^\s*(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)\s*$/.exec(value);
  if (!m) return DEFAULT_FRAME_ASPECT_RATIO;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return DEFAULT_FRAME_ASPECT_RATIO;
  }
  return w / h;
}

/** GET /api/g/{slug}/config */
export interface PropertyConfig {
  /** Display name of the property (tenant config, never hardcoded). */
  name: string;
  /** Optional marketing incentive line shown near contact fields and on confirmation. */
  incentiveText: string;
  /** Whether captions are burned into photos client-side. */
  captionBurnIn: boolean;
  /** Caption burn-in style. Optional over the wire; absent → framesafe default. */
  captionStyle?: CaptionStyle;
  /**
   * Target frame aspect ('W:H', e.g. '4:3', '16:9') for the 'framefill' style.
   * Optional over the wire; absent/invalid → 4:3 (see resolveFrameAspectRatio).
   */
  frameAspect?: string;
  /**
   * Data controller (the host) shown on the privacy notice. Flat fields
   * projected by the config endpoint (#128). Optional over the wire — the notice
   * falls back to the property name / generic copy when absent.
   */
  controllerName?: string;
  /** Controller contact (email or phone) for privacy questions. */
  controllerContact?: string;
  /** Per-property retention window in days (0 = kept indefinitely). Shown on the notice. */
  retentionDays?: number;
  /** Version string of the consent/privacy copy (ties to the consent copyVersion). */
  privacyVersion?: string;
}

/** File descriptor sent to POST /api/g/{slug}/session */
export interface SessionFileRequest {
  name: string;
  type: string;
  size: number;
}

/** POST /api/g/{slug}/session response */
export interface SessionResponse {
  submissionId: string;
  uploads: SessionUpload[];
}

export interface SessionUpload {
  blobPath: string;
  sasUrl: string;
}

/** POST /api/g/{slug}/submit request */
export interface SubmitRequest {
  submissionId: string;
  name: string;
  caption: string;
  email?: string;
  phone?: string;
  consent: boolean;
}

/** Abstraction over the three guest API calls + the SAS upload, so a mock can stand in. */
export interface GuestApi {
  /** Returns null when the slug is unknown (404). */
  fetchConfig(slug: string): Promise<PropertyConfig | null>;
  createSession(slug: string, files: SessionFileRequest[]): Promise<SessionResponse>;
  uploadToSas(
    sasUrl: string,
    blob: Blob,
    contentType: string,
    onProgress: (fraction: number) => void
  ): Promise<void>;
  submit(slug: string, payload: SubmitRequest): Promise<void>;
}

/* ============================================================ privacy / GDPR */

/** Marketing-consent record for a guest (lawful-basis evidence). */
export interface ConsentInfo {
  granted: boolean;
  /** ISO timestamp of the consent decision, or null if never decided. */
  utc: string | null;
  /** Version of the consent copy in force at the time, or null. */
  copyVersion: string | null;
}

/** A guest as shown in the host portal list. */
export interface Guest {
  guestId: string;
  name: string;
  email: string | null;
  phone: string | null;
  consent: ConsentInfo;
  firstSeen: string;
  lastSeen: string;
  stayCount: number;
}

/** Per-property host settings (privacy slice). */
export interface HostSettings {
  /** 0 = keep indefinitely; otherwise auto-purge past lastSeen + retentionDays. */
  retentionDays: number;
}

/** Summary the DELETE endpoint returns after erasing a guest. */
export interface RemovalSummary {
  guestId: string;
  name?: string;
  submissionsRemoved?: number;
  mediaRemoved?: number;
}

/** A blob ready to hand to the browser's download flow. */
export interface DownloadPayload {
  blob: Blob;
  filename: string;
}

/**
 * Host portal API (authenticated; the Static Web App injects the principal and
 * gates /api/host/* to the "authenticated" role — see staticwebapp.config.json).
 */
export interface HostApi {
  listGuests(query?: string): Promise<Guest[]>;
  exportGuest(guestId: string): Promise<DownloadPayload>;
  exportConsentedCsv(): Promise<DownloadPayload>;
  deleteGuest(guestId: string): Promise<RemovalSummary>;
  setConsent(guestId: string, granted: boolean): Promise<void>;
  getSettings(): Promise<HostSettings>;
  updateSettings(settings: HostSettings): Promise<HostSettings>;
}

/** One of a guest's own submissions, as shown on the self-service token view. */
export interface GuestSubmissionSummary {
  submissionId: string;
  caption: string;
  createdUtc: string;
  status: string;
  photoCount?: number;
}

/** The guest's stored data returned by the token-gated self-service endpoint. */
export interface GuestSelfData {
  name: string;
  email: string | null;
  phone: string | null;
  consent: ConsentInfo;
  submissions: GuestSubmissionSummary[];
}

/**
 * Guest self-service API (anonymous, verified by an emailed single-use token).
 * request-link never reveals whether an email exists (neutral response).
 */
export interface SelfServiceApi {
  requestLink(slug: string, email: string): Promise<void>;
  fetchMe(slug: string, token: string): Promise<GuestSelfData>;
  setConsent(slug: string, token: string, granted: boolean): Promise<void>;
  deleteMe(slug: string, token: string): Promise<void>;
}
